import { useState, useCallback } from 'react'
import { apiClient } from '../services/apiClient'

export function useApi() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const execute = useCallback(async (apiCall) => {
    console.log('useApi: Setting loading to true')
    setLoading(true)
    setError(null)

    // Add a safety timeout to prevent loading state from getting stuck
    const timeoutId = setTimeout(() => {
      console.warn('useApi: Request timeout - forcing loading to false')
      setLoading(false)
      setError('Request timeout')
    }, 30000) // 30 second timeout

    try {
      const result = await apiCall()
      clearTimeout(timeoutId)
      console.log('useApi: API call successful, setting loading to false')
      setLoading(false)
      return result
    } catch (err) {
      clearTimeout(timeoutId)
      console.log('useApi: API call failed, setting loading to false', err)
      setError(err.message || 'An error occurred')
      setLoading(false)
      throw err
    }
  }, [])

  return { loading, error, execute, apiClient }
}

// Specific API hooks
export function useQueryProcessing() {
  const { loading, error, execute } = useApi()

  const processQuery = useCallback((query, includeContext = true, signal = null, workspaceId = null) => {
    console.log('Hook processQuery called:', { query, includeContext })
    return execute(() => apiClient.processQuery(query, includeContext, signal, workspaceId))
  }, [execute])

  const processSupervisorQuery = useCallback((query, supervisorConfig, contextEnabled = true, signal = null, sessionId = null, personalContextEnabled = true, workspaceId = null, agenticMode = false) => {
    return execute(() => apiClient.processSupervisorQuery(query, supervisorConfig, contextEnabled, signal, sessionId, personalContextEnabled, workspaceId, agenticMode))
  }, [execute])

  return { processQuery, processSupervisorQuery, loading, error }
}

export function useMemories() {
  const { loading, error, execute } = useApi()

  const getMemories = useCallback((filters = {}, workspaceId = null) => {
    return execute(() => apiClient.getMemories(filters, workspaceId))
  }, [execute])

  const getMemoryStats = useCallback(() => {
    return execute(() => apiClient.getMemoryStats())
  }, [execute])

  const getUserContext = useCallback((query, limit = 10) => {
    return execute(() => apiClient.getUserContext(query, limit))
  }, [execute])

  const updateMemory = useCallback((memoryId, updateData) => {
    return execute(() => apiClient.updateMemory(memoryId, updateData))
  }, [execute])

  const deleteMemory = useCallback((memoryId, workspaceId = null) => {
    return execute(() => apiClient.deleteMemory(memoryId, workspaceId))
  }, [execute])

  const exportMemories = useCallback((filters = null, workspaceId = null) => {
    return execute(() => apiClient.exportMemories(filters, workspaceId))
  }, [execute])

  const importMemories = useCallback((file, mode = 'merge', workspaceId = null) => {
    return execute(() => apiClient.importMemories(file, mode, workspaceId))
  }, [execute])

  const deleteAllMemories = useCallback((workspaceId = null) => {
    return execute(() => apiClient.deleteAllMemories(workspaceId))
  }, [execute])

  return {
    getMemories,
    getMemoryStats,
    getUserContext,
    updateMemory,
    deleteMemory,
    deleteAllMemories,
    exportMemories,
    importMemories,
    loading,
    error
  }
}

export function useKnowledge() {
  const { loading, error, execute } = useApi()

  const getKnowledgeStats = useCallback(() => {
    return execute(() => apiClient.getKnowledgeStats())
  }, [execute])

  const getKnowledgeDocuments = useCallback(() => {
    return execute(() => apiClient.getKnowledgeDocuments())
  }, [execute])

  const connectDocument = useCallback((docId) => {
    return execute(() => apiClient.connectDocument(docId))
  }, [execute])

  return { getKnowledgeStats, getKnowledgeDocuments, connectDocument, loading, error }
}

export function useBackups() {
	const { loading, error, execute } = useApi()

	const getAllBackups = useCallback(() => {
		return execute(() => apiClient.getAllBackups())
	}, [execute])

	const getFullBackups = useCallback(() => {
		return execute (() => apiClient.getFullBackups())
	}, [execute])

	const getIncrementalBackups = useCallback(() => {
		return execute (() => apiClient.getIncrementalBackups())
	}, [execute])

	const getBackupInfo = useCallback((backupName, backupType) => {
		return execute(() => apiClient.getBackupInfo(backupName, backupType))
	}, [execute])

	const getFullBackupSchedule = useCallback(() => {
		return execute(() => apiClient.getBackupSchedule('full'))
	}, [execute])

	const getIncrementalBackupSchedule = useCallback(() => {
		return execute(() => apiClient.getBackupSchedule('incremental'))
	}, [execute])

	const createBackup = useCallback((backupType) => {
		return execute(() => apiClient.createBackup(backupType))
	}, [execute])

	const deleteBackup = useCallback((backupName, backupType) => {
		return execute(() => apiClient.deleteBackup(backupName, backupType))
	}, [execute])

	const restoreBackup = useCallback((backupName, backupType) => {
		return execute(() => apiClient.restoreBackup(backupName, backupType))
	}, [execute])

	return {
		getAllBackups,
		getFullBackups,
		getIncrementalBackups,
		getBackupInfo,
		getFullBackupSchedule,
		getIncrementalBackupSchedule,
		createBackup,
		restoreBackup,
		deleteBackup,
		loading,
		error
	}
}
